"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

const bodyStyle: CSSProperties = {
  margin: 0,
  background: "#f7f3eb",
  color: "#123b34",
  fontFamily: "Arial, Helvetica, sans-serif",
};

const mainStyle: CSSProperties = {
  boxSizing: "border-box",
  display: "grid",
  minHeight: "100vh",
  placeItems: "center",
  overflowX: "hidden",
  padding: "32px 20px",
};

const cardStyle: CSSProperties = {
  boxSizing: "border-box",
  width: "100%",
  maxWidth: "640px",
  border: "1px solid #e4d7c3",
  borderRadius: "24px",
  background: "#ffffff",
  padding: "clamp(24px, 6vw, 44px)",
  boxShadow: "0 20px 60px rgba(49, 39, 24, 0.12)",
};

const actionRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  marginTop: "28px",
};

const primaryActionStyle: CSSProperties = {
  boxSizing: "border-box",
  display: "inline-flex",
  minHeight: "44px",
  alignItems: "center",
  justifyContent: "center",
  border: 0,
  borderRadius: "999px",
  background: "#123b34",
  color: "#ffffff",
  cursor: "pointer",
  font: "700 14px/1.3 Arial, Helvetica, sans-serif",
  padding: "12px 20px",
};

const linkStyle: CSSProperties = {
  boxSizing: "border-box",
  display: "inline-flex",
  minHeight: "44px",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #b88a2b",
  borderRadius: "999px",
  color: "#123b34",
  fontSize: "14px",
  fontWeight: 700,
  padding: "11px 20px",
  textDecoration: "none",
};

export default function GlobalError({ reset }: GlobalErrorProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  function retry() {
    if (isRetrying) return;
    setIsRetrying(true);
    reset();
  }

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Page unavailable | Singing Attitude UK</title>
      </head>
      <body style={bodyStyle}>
        <main role="alert" aria-labelledby="global-error-heading" style={mainStyle}>
          <section style={cardStyle}>
            <p style={{ margin: "0 0 22px", color: "#80611f", fontSize: "13px", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Singing Attitude UK
            </p>
            <h1
              ref={headingRef}
              id="global-error-heading"
              tabIndex={-1}
              style={{ margin: 0, fontFamily: "Georgia, Times New Roman, serif", fontSize: "clamp(30px, 8vw, 46px)", lineHeight: 1.12, outline: "none" }}
            >
              We could not load this page.
            </h1>
            <p style={{ margin: "18px 0 0", color: "#49564f", fontSize: "17px", lineHeight: 1.65 }}>
              Please try again. If the problem continues, return home or contact us for help.
            </p>
            <div style={actionRowStyle}>
              <button
                type="button"
                onClick={retry}
                disabled={isRetrying}
                style={{ ...primaryActionStyle, cursor: isRetrying ? "wait" : "pointer", opacity: isRetrying ? 0.72 : 1 }}
              >
                {isRetrying ? "Trying again…" : "Retry this page"}
              </button>
              {/* This root fallback must work without the application router. */}
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a href="/" style={linkStyle}>Return home</a>
              <a href="/contact" style={linkStyle}>Contact us</a>
            </div>
          </section>
        </main>
      </body>
    </html>
  );
}
